import {NgTemplateOutlet} from "@angular/common"
import {ChangeDetectionStrategy, Component, input} from "@angular/core"
import {MatTooltipModule} from "@angular/material/tooltip"
import {TippyDirective} from "@ngneat/helipopper"
import {JobTaskCascadePopoverComponent} from "@platform/components/jobs/job-task-cascade/job-task-cascade-popover.component"
import {CascadeStep, PlatformIcon, PLATFORM, STATUS} from "@platform/components/jobs/job-task-cascade/job-task-cascade.model"

/**
 * Renders a task's resource cascade as a caret-separated row of icons: a Google Cloud platform glyph per
 * step with a small cost-tier suffix ($…$$$$), coloured by status, each opening a rich popover on hover.
 * Purely presentational — the ordering, status and per-step events are all resolved server-side (the
 * `cascade` field on JobTask). A pinned task shows a pin + its single group rather than a cascade.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-task-cascade",
    templateUrl: "./job-task-cascade.component.html",
    styleUrls: ["./job-task-cascade.component.scss"],
    imports: [MatTooltipModule, TippyDirective, NgTemplateOutlet],
})
export class JobTaskCascadeComponent {
    readonly $cascade = input.required<readonly CascadeStep[]>({alias: "cascade"})
    /** When the task is pinned to a resource group, the cascade is bypassed — show a pin + the one group. */
    readonly $pinned = input(false, {alias: "pinned"})

    readonly pinTooltip = "Pinned to this resource group — the resource cascade is bypassed, so the task runs only here."
    protected readonly Popover = JobTaskCascadePopoverComponent

    statusClass(step: CascadeStep): string {
        return STATUS[step.status].class
    }
    platformIcon(step: CascadeStep): PlatformIcon {
        return PLATFORM[step.platform].icon
    }
    /** Filled dots (of 2) for the provisioning-mode indicator, or null when the platform has no mode dots. */
    dots(step: CascadeStep): number | null {
        return PLATFORM[step.platform].dots
    }
}
